import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { historyApi, type HistoryListItem } from '@/api/history';
import { CasinoButton } from '@/components/CasinoButton';
import { SignPanel } from '@/components/SignPanel';
import { formatDollars } from '@/lib/payouts';
import { cn } from '@/lib/utils';

export function HistoryList() {
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['history', page],
    queryFn: () => historyApi.list(page),
  });

  return (
    <main className="mx-auto max-w-2xl px-5 py-6 space-y-5">
      <header>
        <h1 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
          History
        </h1>
        <p className="text-sm text-cabinet-cream/60 mt-1">
          Past tournaments and your results.
        </p>
      </header>

      {q.isLoading ? (
        <p className="text-cabinet-cream/60">Loading…</p>
      ) : q.data?.items.length === 0 ? (
        <section className="cabinet-card p-6 text-center space-y-2">
          <p className="text-cabinet-cream/70 text-sm">
            No completed tournaments yet.
          </p>
        </section>
      ) : (
        <>
          <ul className="space-y-2">
            {q.data?.items.map((t) => (
              <li key={t.id}>
                <Item t={t} />
              </li>
            ))}
          </ul>

          {q.data?.hasMore && (
            <div className="flex justify-center">
              <CasinoButton
                variant="ghost"
                onClick={() => setPage((p) => p + 1)}
                disabled={q.isFetching}
              >
                {q.isFetching ? 'Loading…' : 'Load more'}
              </CasinoButton>
            </div>
          )}
        </>
      )}
    </main>
  );
}

function Item({ t }: { t: HistoryListItem }) {
  return (
    <Link
      to={`/history/${t.id}`}
      className="block rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 hover:ring-cabinet-gold/40 transition px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-cabinet-cream/50">
            {t.machineName}
          </p>
          <SignPanel size="sm" className="!block mt-1 max-w-full">
            <h3 className="text-marquee text-xl uppercase leading-none truncate">
              {t.name}
            </h3>
          </SignPanel>
          <p className="text-[10px] text-cabinet-cream/50 mt-1">
            {t.completedAt ? new Date(t.completedAt).toLocaleDateString() : '—'}
          </p>
        </div>
        <ResultBadge result={t.myResult} />
      </div>
    </Link>
  );
}

function ResultBadge({ result }: { result: HistoryListItem['myResult'] }) {
  if (!result) {
    return (
      <span className="text-[10px] uppercase tracking-widest text-cabinet-cream/40">
        Did not play
      </span>
    );
  }
  if (result.place === null) {
    return (
      <span className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
        Played
      </span>
    );
  }
  return (
    <div className="text-right">
      <p
        className={cn(
          'font-display uppercase tracking-widest text-xs',
          result.place === 1
            ? 'text-cabinet-gold-bright'
            : result.place === 2
              ? 'text-cabinet-cream'
              : 'text-cabinet-flame-orange',
        )}
      >
        {ordinal(result.place)} place
      </p>
      <p className="font-display text-cabinet-gold-bright text-base tabular-nums">
        {formatDollars(result.amountCents)}
      </p>
      <p
        className={cn(
          'text-[10px] uppercase tracking-widest',
          result.paid ? 'text-cabinet-gold-bright' : 'text-cabinet-flame-orange',
        )}
      >
        {result.paid ? 'Paid' : 'Uncollected'}
      </p>
    </div>
  );
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
